import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";

const NotFound = () => {
  const navigate = useNavigate();
  return (
    <div data-testid="not-found-page" className="p-8 max-w-xl">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">404</div>
      <h1 className="font-heading text-4xl font-extrabold text-white mt-2">Page not found</h1>
      <p className="text-zinc-400 mt-3">That link doesn't exist in UC Engineer Pro.</p>
      <button data-testid="not-found-home" onClick={() => navigate("/")} className="mt-5 h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold">Back to Overview</button>
    </div>
  );
};
import { Layout } from "@/components/Layout";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Overview from "@/pages/Overview";
import Wizard from "@/pages/Wizard";
import HealthChecks from "@/pages/HealthChecks";
import HealthCheckDetail from "@/pages/HealthCheckDetail";
import Migration from "@/pages/Migration";
import MigrationExport from "@/pages/MigrationExport";
import Firmware from "@/pages/Firmware";
import Licenses from "@/pages/Licenses";
import Certificates from "@/pages/Certificates";
import Tools from "@/pages/Tools";
import E911 from "@/pages/E911";
import Rooms from "@/pages/Rooms";
import Qos from "@/pages/Qos";
import DR from "@/pages/DR";
import Site360 from "@/pages/Site360";
import Changes from "@/pages/Changes";
import Runbook from "@/pages/Runbook";
import Scoreboard from "@/pages/Scoreboard";
import ScoreboardExport from "@/pages/ScoreboardExport";
import ReadinessReport from "@/pages/ReadinessReport";
import WaveCutover from "@/pages/WaveCutover";
import GetStarted from "@/pages/GetStarted";
import Capabilities from "@/pages/Capabilities";
import Settings from "@/pages/Settings";
import Team from "@/pages/Team";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import InviteAccept from "@/pages/InviteAccept";
import Assistant from "@/pages/Assistant";
import Reports from "@/pages/Reports";
import ReportView from "@/pages/ReportView";
import Analytics from "@/pages/Analytics";
import Support from "@/pages/Support";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import RegionBlocked from "@/pages/RegionBlocked";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import { CookieConsent } from "@/components/CookieConsent";
import { Toaster } from "@/components/ui/sonner";

function AppShell() {
  return (
    <ProtectedRoute>
      <Layout>
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/diagnostics" element={<Dashboard />} />
          <Route path="/wizard/:id" element={<Wizard />} />
          <Route path="/health-checks" element={<HealthChecks />} />
          <Route path="/health-checks/:id" element={<HealthCheckDetail />} />
          <Route path="/migration" element={<Migration />} />
          <Route path="/runbook" element={<Runbook />} />
          <Route path="/scoreboard" element={<Scoreboard />} />
          <Route path="/waves" element={<WaveCutover />} />
          <Route path="/start" element={<GetStarted />} />
          <Route path="/firmware" element={<Firmware />} />
          <Route path="/licenses" element={<Licenses />} />
          <Route path="/certificates" element={<Certificates />} />
          <Route path="/e911" element={<E911 />} />
          <Route path="/rooms" element={<Rooms />} />
          <Route path="/dr" element={<DR />} />
          <Route path="/site360" element={<Site360 />} />
          <Route path="/changes" element={<Changes />} />
          <Route path="/qos" element={<Qos />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/assistant" element={<Assistant />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/support" element={<Support />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/team" element={<Team />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/admin" element={<Navigate to="/analytics" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </ProtectedRoute>
  );
}

function AppRouter() {
  const location = useLocation();
  // Google OAuth returns to {origin}/#session_id=... — handle it before anything else.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public standalone pages */}
      <Route path="/report/:shareId" element={<ReportView />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/invite" element={<InviteAccept />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/region-blocked" element={<RegionBlocked />} />
      {/* Authed export pages (cookies sent automatically) */}
      <Route path="/migration/export" element={<MigrationExport />} />
      <Route path="/scoreboard/export" element={<ScoreboardExport />} />
      <Route path="/readiness/report" element={<ReadinessReport />} />
      <Route path="/capabilities" element={<Capabilities />} />
      {/* Gated app shell */}
      <Route path="*" element={<AppShell />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <AuthProvider>
          <AnalyticsTracker />
          <AppRouter />
          <CookieConsent />
        </AuthProvider>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
